import {isFeatureEnabled} from 'feature-flags';
import {
  Alpha3CountryCode,
  DataStatus,
  Form,
  FormField,
  FormSubmitHandler,
  Label,
  PhoneNumber,
  showNotification,
  TextInput,
} from 'platform/components';
import {Box, Grid, GridItem, Show, Text, Space, VStack} from 'platform/foundation';

import {useState} from 'react';

import {head, isNotNil} from 'ramda';

import {useGetTenantQuery, useSetEmployeePhotoMutation} from '@dms/api/core';
import {
  useGetCurrentUserInfoQuery,
  useGetEmployeeLinkQuery,
  usePatchCurrentUserMutation,
} from '@dms/api/customer';
import featureFlags from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {handleApiError, usePhoneNumbers} from '@dms/shared';

import {ApiError} from 'shared/web';

import {MyProfileFooter} from '../../../components/MyProfileFooter';
import {
  PersonalInformationFormSchema,
  PersonalInformationFormSchemaType,
} from '../schemas/PersonalInformationFormSchema';
import {PersonalInformationAvatar} from './PersonalInformationAvatar';

export function PersonalInformationForm() {
  const {countriesOptions} = usePhoneNumbers();
  const {
    data: user,
    isLoading: isUserLoading,
    isError: hasUserError,
  } = useGetCurrentUserInfoQuery();
  const {data: tenant, isLoading: isTenantLoading, isError: hasTenantError} = useGetTenantQuery();

  const [patchUser] = usePatchCurrentUserMutation();
  const [setEmployeePhoto] = useSetEmployeePhotoMutation();

  const {data: employeeLink, isLoading: isEmployeeLinkLoading} = useGetEmployeeLinkQuery(
    {contextId: user?.id ?? '', contextTarget: 'USER'},
    {skip: !user?.id}
  );

  const employeeId = head(employeeLink ?? [])?.employeeId;
  const hasEmployeeId = isNotNil(employeeId);

  const [fileId, setFileId] = useState<string | null>(null);

  const handleSubmit: FormSubmitHandler<PersonalInformationFormSchemaType> = async (
    values,
    _setErrors,
    reset,
    blockerContext
  ) => {
    const shouldSubmitPhoto =
      hasEmployeeId && isNotNil(fileId) && isFeatureEnabled(featureFlags.MY_PROFILE_AVATAR);
    const args = {
      updateCurrentUserRequestBody: {
        firstName: values.firstName,
        lastName: values.lastName,
        phoneNumber: values.phoneNumber,
      },
    };

    return await patchUser(args)
      .unwrap()
      .then(async () => {
        if (shouldSubmitPhoto) {
          await setEmployeePhoto({
            employeeId: employeeId as string,
            setEmployeePhotoRequestBody: {
              fileId,
            },
          })
            .unwrap()
            .then(() => setFileId(null))
            .catch((e: ApiError) =>
              handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
            );
        }
      })
      .then(() => {
        showNotification.success(i18n.t('general.notifications.changesSuccessfullySaved'));
        reset(undefined, {keepValues: true});
      })
      .catch((e: ApiError) =>
        handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
      );
  };

  const isLoading = isUserLoading || isTenantLoading || isEmployeeLinkLoading;
  const hasError = hasUserError || hasTenantError;

  const defaultValues = {
    firstName: user?.firstName ?? '',
    lastName: user?.lastName ?? '',
    phoneNumber: (user?.phoneNumber as PhoneNumber) ?? {
      prefix: '',
      number: '',
      countryCode: tenant?.country,
    },
  };

  return (
    <DataStatus isLoading={isLoading} isError={hasError} minHeight={50}>
      <Box maxWidth={200}>
        <VStack spacing={6}>
          <Form<PersonalInformationFormSchemaType>
            shouldWatchForUnsavedChanges
            defaultValues={defaultValues}
            experimentalZodSchema={PersonalInformationFormSchema}
            mode="onBlur"
            onSubmit={handleSubmit}
          >
            {(control, formApi) => (
              <>
                <Grid columns={2} spacing={6}>
                  <GridItem span={2}>
                    <Show when={hasEmployeeId} whenFeatureEnabled={featureFlags.MY_PROFILE_AVATAR}>
                      <PersonalInformationAvatar
                        data-testid="employee-avatar-select"
                        setFileId={setFileId}
                        fileId={fileId}
                      />
                    </Show>
                  </GridItem>
                  <GridItem>
                    <FormField
                      control={control}
                      isRequired
                      type="text"
                      name="firstName"
                      label={i18n.t('entity.person.labels.firstName')}
                    />
                  </GridItem>
                  <GridItem>
                    <FormField
                      control={control}
                      isRequired
                      type="text"
                      name="lastName"
                      label={i18n.t('entity.person.labels.lastName')}
                    />
                  </GridItem>

                  <GridItem>
                    <TextInput
                      label={i18n.t('general.labels.email')}
                      value={user?.email ?? ''}
                      isDisabled
                    />
                  </GridItem>
                  <GridItem>
                    <FormField
                      type="phone"
                      control={control}
                      name="phoneNumber"
                      defaultCountry={tenant?.country as Alpha3CountryCode}
                      label={i18n.t('entity.person.labels.phoneNumber')}
                      countries={countriesOptions}
                    />
                  </GridItem>
                </Grid>

                <Space vertical={20} />

                <VStack spacing={6}>
                  <VStack spacing={1}>
                    <Label>{`${i18n.t('page.settings.labels.assignedRole')}:`}</Label>
                    <Text>
                      {user?.roles?.length
                        ? user.roles
                            .map((role) => role.title)
                            .join(', ')
                            .toString()
                        : '-'}
                    </Text>
                  </VStack>

                  <VStack spacing={1}>
                    <Label>{`${i18n.t('page.settings.labels.assignedBranches')}:`}</Label>
                    <Text>
                      {user?.branches?.length
                        ? user.branches
                            .map((branch) => branch.name)
                            .join(', ')
                            .toString()
                        : '-'}
                    </Text>
                  </VStack>
                </VStack>
                <MyProfileFooter
                  actions={[
                    {
                      type: 'button',
                      onClick: () => formApi.reset(defaultValues),
                      title: i18n.t('general.actions.discardChanges'),
                      variant: 'secondary',
                    },
                    {
                      type: 'form-button',
                      control,
                      buttonType: 'submit',
                      title: i18n.t('general.actions.saveChanges'),
                    },
                  ]}
                />
              </>
            )}
          </Form>
        </VStack>
      </Box>
    </DataStatus>
  );
}
